import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { CalendarIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Helper to generate class names conditionally
function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

/**
 * Component for setting the project start date with calendar interface
 */
const ProjectStartDatePicker = ({ initialDate, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialDate || '');
  const [prevDate, setPrevDate] = useState(initialDate || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  
  // Calendar state
  const [month, setMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  
  // Generate calendar days whenever month or selected date changes
  useEffect(() => {
    generateCalendarDays(month);
  }, [month, selectedDate]);
  
  // Initialize from initialDate
  useEffect(() => {
    if (initialDate) {
      setSelectedDate(initialDate);
      setPrevDate(initialDate);
      
      // Set calendar to show month of initial date
      try {
        // Parse date parts directly to avoid timezone issues
        const [year, month, day] = initialDate.split('-').map(Number);
        // Use noon time to avoid timezone issues
        setMonth(new Date(year, month - 1, 1, 12, 0, 0));
      } catch (err) {
        // If date parsing fails, just use current month
        const now = new Date();
        setMonth(new Date(now.getFullYear(), now.getMonth(), 1, 12, 0, 0));
      }
    }
  }, [initialDate]);
  
  // Generate array of day objects for the calendar
  const generateCalendarDays = (targetMonth) => {
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    
    // Get first day of month and how many days to show from previous month
    const firstDayOfMonth = new Date(year, month, 1);
    const dayOfWeek = firstDayOfMonth.getDay();
    const daysFromPrevMonth = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
    
    // Get last day of month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Calculate days to show from next month to complete the grid
    const daysFromNextMonth = 42 - (daysFromPrevMonth + daysInMonth); // 6 rows of 7 days
    
    const days = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
      const date = new Date(year, month - 1, i);
      days.push({
        date: formatDate(date),
        isCurrentMonth: false,
        isToday: isDateToday(date),
        isSelected: isDateSelected(date, selectedDate)
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date: formatDate(date),
        isCurrentMonth: true,
        isToday: isDateToday(date),
        isSelected: isDateSelected(date, selectedDate)
      });
    }
    
    // Add days from next month
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date: formatDate(date),
        isCurrentMonth: false,
        isToday: isDateToday(date),
        isSelected: isDateSelected(date, selectedDate)
      });
    }
    
    setCalendarDays(days);
  };
  
  // Format date as YYYY-MM-DD ensuring consistent handling
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Check if a date is today
  const isDateToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  // Check if a date is selected
  const isDateSelected = (date, selectedDateStr) => {
    if (!selectedDateStr) return false;
    
    try {
      // Parse the date parts directly
      const [year, month, day] = selectedDateStr.split('-').map(Number);
      
      // Compare date parts directly to avoid timezone issues
      const isSelected = date.getDate() === day &&
             date.getMonth() === (month - 1) &&
             date.getFullYear() === year;
      
      return isSelected;
    } catch (err) {
      console.error('Error checking if date is selected:', err);
      return false;
    }
  };
  
  // Convert date to display format (Month YYYY)
  const getDisplayDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      // Use the date parts directly to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      
      // Create date object with year, month (0-indexed), day with a noon time to avoid timezone issues
      const date = new Date(year, month - 1, day, 12, 0, 0);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' });
    } catch (err) {
      return dateString;
    }
  };
  
  // Get month name and year for calendar header
  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  // Handle previous month button
  const handlePrevMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  };
  
  // Handle next month button
  const handleNextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  };
  
  // Handle day selection
  const handleDayClick = (date) => {
    console.log('Selected date:', date);
    setSelectedDate(date);
    // Force calendar refresh
    generateCalendarDays(month);
  };

  // Handle click to enter edit mode
  const handleEditClick = () => {
    setPrevDate(selectedDate);
    setIsEditing(true);
  };

  // Handle save
  const handleSave = async () => {
    // If date hasn't changed, just exit edit mode
    if (selectedDate === prevDate) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      // Import is inside to avoid circular dependencies
      const { updateProjectStartDate } = await import('../../utils/api');
      
      // Call the API to update the project start date
      const result = await updateProjectStartDate(selectedDate);
      
      if (result.success) {
        setIsEditing(false);
        // Call the onUpdate callback with the new date
        if (onUpdate) {
          onUpdate(selectedDate);
        }
      } else {
        setError('Failed to update project start date');
      }
    } catch (error) {
      console.error('Error updating project start date:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedDate(prevDate);
    setIsEditing(false);
    setError(null);
  };

  if (isEditing) {
    return (
      <div className="relative border border-gray-300 dark:border-gray-600 rounded-md p-4 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Project Start Date</h3>
        
        <div className="flex items-center justify-between text-gray-900 dark:text-gray-100">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">Previous month</span>
            <ChevronLeftIcon className="size-5" aria-hidden="true" />
          </button>
          <div className="flex-auto text-sm font-semibold text-center">{getMonthName(month)}</div>
          <button
            type="button"
            onClick={handleNextMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">Next month</span>
            <ChevronRightIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
        
        <div className="mt-6 grid grid-cols-7 text-xs/6 text-gray-500 dark:text-gray-400">
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
          <div>S</div>
        </div>
        
        <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-700 text-sm shadow-sm ring-1 ring-gray-200 dark:ring-gray-700">
          {calendarDays.map((day, dayIdx) => (
            <button
              key={day.date}
              type="button"
              onClick={() => handleDayClick(day.date)}
              className={classNames(
                'py-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 focus:z-10',
                day.isCurrentMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700',
                (day.isSelected || day.isToday) && 'font-semibold',
                !day.isSelected && day.isCurrentMonth && !day.isToday && 'text-gray-900 dark:text-gray-100',
                !day.isSelected && !day.isCurrentMonth && !day.isToday && 'text-gray-400 dark:text-gray-500',
                day.isToday && !day.isSelected && 'text-indigo-600 dark:text-indigo-400',
                dayIdx === 0 && 'rounded-tl-lg',
                dayIdx === 6 && 'rounded-tr-lg',
                dayIdx === calendarDays.length - 7 && 'rounded-bl-lg',
                dayIdx === calendarDays.length - 1 && 'rounded-br-lg',
              )}
            >
              <time
                dateTime={day.date}
                className={classNames(
                  'mx-auto flex size-7 items-center justify-center rounded-full',
                  day.isSelected && day.isToday && 'bg-indigo-600 text-white',
                  day.isSelected && !day.isToday && 'bg-gray-900 dark:bg-indigo-600 text-white',
                )}
              >
                {day.date.split('-').pop().replace(/^0/, '')}
              </time>
            </button>
          ))}
        </div>
        
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUpdating}
            className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200"
          >
            <XMarkIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            disabled={isUpdating || !selectedDate}
            className="inline-flex items-center rounded-md bg-indigo-600 dark:bg-indigo-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
          >
            <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
            Save
          </button>
        </div>
        
        {isUpdating && (
          <div className="absolute right-2 top-4">
            <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">Saving...</span>
          </div>
        )}
        
        {error && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div 
      className="border border-gray-200 dark:border-gray-700 rounded-md p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group dark:bg-gray-800"
      onClick={handleEditClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Project Start Date</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {getDisplayDate(selectedDate)}
          </p>
        </div>
        <CalendarIcon 
          className="h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" 
          aria-hidden="true" 
        />
      </div>
    </div>
  );
};

export default ProjectStartDatePicker;